import { type AnchorablePayment, type FixedPayment, type PaymentFrequencyRequest, WeekDay } from '@domains/subscription/priv/types'
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

interface PaymentFrequencyProps {
  paymentFrequency: PaymentFrequencyRequest | undefined
  onChange: (paymentFrequency: PaymentFrequencyRequest) => void
}

export const PaymentFrequencyField = ({ paymentFrequency, onChange }: PaymentFrequencyProps) => {
  const frequencyTypeOptions: Record<PaymentFrequencyRequest['type'], string> = {
    anchorable: 'Anchored to signup',
    fixed: 'Fixed',
  }

  const handleTypeChange = (type: PaymentFrequencyRequest['type']) => {
    switch (type) {
      case 'anchorable': {
        return onChange({ type: 'anchorable', period: 'monthly' })
      }

      case 'fixed': {
        return onChange({ type: 'fixed', frequency: 'monthly', dayOfMonth: 28 })
      }
    }
  }

  const getPaymentFrequencyComponent = () => {
    switch (paymentFrequency?.type) {
      case 'anchorable':
        return <AnchorablePaymentFrequency paymentFrequency={paymentFrequency} onChange={onChange} />
      case 'fixed':
        return <FixedPaymentFrequency paymentFrequency={paymentFrequency} onChange={onChange} />
      default:
        return null
    }
  }

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <FormControl fullWidth>
        <InputLabel>Payment frequency type</InputLabel>
        <Select
          slotProps={{ input: { 'aria-label': 'payment frequency type' } }}
          label="Payment frequency type"
          value={paymentFrequency?.type ?? ''}
          onChange={(event) => handleTypeChange(event.target.value as PaymentFrequencyRequest['type'])}
        >
          {Object.entries(frequencyTypeOptions).map(([type, label]) => (
            <MenuItem key={type} value={type}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {getPaymentFrequencyComponent()}
    </Stack>
  )
}

interface AnchorablePaymentFrequencyOptions {
  paymentFrequency: AnchorablePayment
  onChange: (paymentFrequency: AnchorablePayment) => void
}

const AnchorablePaymentFrequency = ({ paymentFrequency, onChange }: AnchorablePaymentFrequencyOptions) => {
  const options: Record<FixedPayment['frequency'], string> = {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    yearly: 'Yearly',
  }

  return (
    <FormControl fullWidth>
      <InputLabel>Payment frequency</InputLabel>
      <Select
        slotProps={{ input: { 'aria-label': 'payment frequency' } }}
        label="Payment frequency"
        value={paymentFrequency.period}
        onChange={(event) => {
          onChange({ ...paymentFrequency, period: event.target.value as AnchorablePayment['period'] })
        }}
      >
        {Object.entries(options).map(([key, value]) => (
          <MenuItem key={key} value={key}>
            {value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

interface FixedPaymentFrequencyOptions {
  paymentFrequency: FixedPayment
  onChange: (paymentFrequency: FixedPayment) => void
}

const FixedPaymentFrequency = ({ paymentFrequency, onChange }: FixedPaymentFrequencyOptions) => {
  const options: Record<FixedPayment['frequency'], string> = {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    yearly: 'Yearly',
  }

  const weekdayOptions: Record<WeekDay, string> = {
    [WeekDay.Monday]: 'Monday',
    [WeekDay.Tuesday]: 'Tuesday',
    [WeekDay.Wednesday]: 'Wednesday',
    [WeekDay.Thursday]: 'Thursday',
    [WeekDay.Friday]: 'Friday',
    [WeekDay.Saturday]: 'Saturday',
    [WeekDay.Sunday]: 'Sunday',
  }

  const handleFrequencyChange = (frequency: FixedPayment['frequency']) => {
    switch (frequency) {
      case 'daily': {
        return onChange({ type: 'fixed', frequency: 'daily' })
      }
      case 'weekly': {
        return onChange({ type: 'fixed', frequency: 'weekly', dayOfWeek: WeekDay.Monday })
      }
      case 'monthly': {
        return onChange({ type: 'fixed', frequency: 'monthly', dayOfMonth: 28 })
      }
      case 'yearly': {
        return onChange({ type: 'fixed', frequency: 'yearly', dayOfYear: 1 })
      }
    }
  }

  const handleDayChange = ({ type, value }: DayOfSource) => {
    switch (type) {
      case 'weekly':
        return onChange({ ...paymentFrequency, frequency: 'weekly', dayOfWeek: value })
      case 'monthly':
        return onChange({ ...paymentFrequency, frequency: 'monthly', dayOfMonth: value })
      case 'yearly':
        return onChange({ ...paymentFrequency, frequency: 'yearly', dayOfYear: value })
    }
  }

  const getDayValue = (frequency: FixedPayment) => {
    switch (frequency.frequency) {
      case 'daily':
        return undefined
      case 'weekly':
        return frequency.dayOfWeek
      case 'monthly':
        return frequency.dayOfMonth
      case 'yearly':
        return frequency.dayOfYear
    }
  }

  const getDayLabel = (frequency: FixedPayment) => {
    switch (frequency.frequency) {
      case 'daily':
        return undefined
      case 'weekly':
        return weekdayOptions[frequency.dayOfWeek]
      case 'monthly':
        return `Day of month`
      case 'yearly':
        return `Day of year (${dayjs()
          .startOf('year')
          .add(frequency.dayOfYear - 1, 'day')
          .format('Do [day of] MMMM')})`
    }
  }

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <FormControl fullWidth>
        <InputLabel>Payment frequency</InputLabel>
        <Select
          slotProps={{ input: { 'aria-label': 'payment frequency' } }}
          label="Payment frequency"
          value={paymentFrequency.frequency}
          onChange={(event) => {
            handleFrequencyChange(event.target.value as FixedPayment['frequency'])
          }}
        >
          {Object.entries(options).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {paymentFrequency.frequency !== 'daily' && paymentFrequency.frequency !== 'weekly' && (
        <TextField
          fullWidth
          type="number"
          label={getDayLabel(paymentFrequency)}
          value={getDayValue(paymentFrequency) ?? ''}
          onChange={(event) => {
            handleDayChange({ type: paymentFrequency.frequency, value: Number(event.target.value) })
          }}
        />
      )}
      {paymentFrequency.frequency === 'weekly' && (
        <FormControl fullWidth>
          <InputLabel>{getDayLabel(paymentFrequency)}</InputLabel>
          <Select
            slotProps={{ input: { 'aria-label': 'payment frequency' } }}
            label={getDayLabel(paymentFrequency)}
            value={getDayValue(paymentFrequency)}
            onChange={(event) => {
              handleDayChange({ type: 'weekly', value: event.target.value as WeekDay })
            }}
          >
            {Object.entries(weekdayOptions).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  )
}

type DayOfSource = { type: 'weekly'; value: WeekDay } | { type: 'monthly'; value: number } | { type: 'yearly'; value: number }
